import styled, { css } from "styled-components";
import { rawColors } from "../../../design-system/tokens/colors";
import { borderRadius } from "../../../design-system/tokens/borders";
import { typographyMixin } from "../../../design-system/tokens/typography";
import type { SectionToggleRowDepth } from "./SectionToggleRow.types";
import { duration, easing } from "../../../design-system/tokens/transitions";

/**
 * Depth-driven styling only — never a JSX branch. Figma 6515:241220 /
 * 6541:75295 (depth 0 = parent widget row) and their nested child rows
 * (depth 1). Neither the 6px icon↔label gap nor the 26px child indent has a
 * spacing token (4px-step scale) — authored in rem per CLAUDE.md §"Units".
 */
const ROW_STYLE: Record<
  SectionToggleRowDepth,
  { height: string; padding: string; typography: ReturnType<typeof typographyMixin> }
> = {
  0: {
    height: "1.625rem", // 26px = 24px content + 2×1px
    padding: "0.0625rem 0.25rem", // 1px 4px
    typography: typographyMixin("body", 3),
  },
  1: {
    height: "1.25rem", // 20px = 18px content + 2×1px
    padding: "0.0625rem 6.25rem 0.0625rem 1.625rem", // 1px 100px 1px 26px
    typography: typographyMixin("caption", 4),
  },
};

export const LeftCluster = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem; // 6px — no matching spacing-scale token
  min-width: 0;
`;

export const IconWrap = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  width: 1rem;
  height: 1rem;
  color: ${rawColors.text.mediumEmphasis};
`;

export const Label = styled.span<{
  $depth: SectionToggleRowDepth;
  $highlighted: boolean;
}>`
  ${({ $depth }) => ROW_STYLE[$depth].typography}
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ $depth, $highlighted }) =>
    // Depth-1 (child) rows never take the interactive-highlight colour —
    // Figma's KPI "Order date" stays high-emphasis even while expanded.
    $depth === 0 && $highlighted
      ? rawColors.text.interactive
      : $depth === 0
        ? rawColors.text.highEmphasis
        : rawColors.text.mediumEmphasis};
`;

export const Row = styled.div<{ $depth: SectionToggleRowDepth }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: ${({ $depth }) => ROW_STYLE[$depth].height};
  padding: ${({ $depth }) => ROW_STYLE[$depth].padding};
  border-radius: ${borderRadius.md};

  &:hover {
    background-color: ${rawColors.structural[100]};
  }

  /* Hover previews the same Body-3 / text.interactive style the row takes
   * when its widget is selected on canvas — depth-1 rows are excluded for
   * the same reason $highlighted skips them above. */
  &:hover ${Label} {
    ${({ $depth }) =>
      $depth === 0
        ? css`
            color: ${rawColors.text.interactive};
          `
        : ""}
  }
`;

export const CaretButton = styled.button`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 0.5rem;
  height: 1rem;
  padding: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${rawColors.text.mediumEmphasis};
`;

export const CaretIcon = styled.svg<{ $expanded: boolean }>`
  width: 0.5rem;
  height: 0.25rem;
  transition: transform ${duration.fast} ${easing.smooth};
  ${({ $expanded }) =>
    $expanded
      ? css`
          transform: none;
        `
      : css`
          transform: rotate(-90deg);
        `}
`;
